import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Lifebuoy, ChatText, Plus } from "@phosphor-icons/react";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Support() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", category: "general", description: "", priority: "medium" });

  const load = () => api.get("/support/tickets").then((r) => setTickets(r.data));
  useEffect(() => { load(); }, []);

  const loadOne = async (id) => {
    const r = await api.get(`/support/tickets/${id}`);
    setSelected(r.data);
  };

  const createTicket = async () => {
    if (!form.subject.trim() || !form.description.trim()) return toast.error("Subject and description required");
    try { await api.post("/support/tickets", form); toast.success("Ticket submitted"); setOpen(false); setForm({ subject:"", category:"general", description:"", priority:"medium" }); load(); }
    catch { toast.error("Could not submit"); }
  };
  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    try { await api.post(`/support/tickets/${selected.id}/reply`, { message: reply }); setReply(""); await loadOne(selected.id); load(); }
    catch { toast.error("Failed"); }
  };
  const setStatus = async (status) => {
    if (!selected) return;
    try { await api.patch(`/support/tickets/${selected.id}/status?status=${status}`); toast.success("Updated"); await loadOne(selected.id); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Lifebuoy size={28} className="text-blue-600" />
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">{isAdmin ? "Support inbox" : "Help centre"}</h1>
        </div>
        {!isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="ticket-new-btn" className="bg-blue-600 hover:bg-blue-700"><Plus size={16} className="mr-1"/> New ticket</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Submit a support ticket</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <div><Label className="label-caps">Subject</Label><Input data-testid="ticket-subject" value={form.subject} onChange={(e)=>setForm({...form, subject: e.target.value})} className="h-11 mt-1.5" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-caps">Category</Label>
                    <Select value={form.category} onValueChange={(v)=>setForm({...form, category: v})}>
                      <SelectTrigger data-testid="ticket-cat" className="h-11 mt-1.5"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="booking">Booking</SelectItem>
                        <SelectItem value="payment">Payment</SelectItem>
                        <SelectItem value="account">Account</SelectItem>
                        <SelectItem value="bug">Bug report</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-caps">Priority</Label>
                    <Select value={form.priority} onValueChange={(v)=>setForm({...form, priority: v})}>
                      <SelectTrigger data-testid="ticket-priority" className="h-11 mt-1.5"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label className="label-caps">Description</Label><Textarea data-testid="ticket-desc" rows={5} value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} className="mt-1.5"/></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                <Button data-testid="ticket-submit-btn" onClick={createTicket} className="bg-blue-600 hover:bg-blue-700">Submit ticket</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-4 h-[600px]">
        <div className="card-tactile p-2 overflow-y-auto">
          {tickets.length === 0 && <div className="text-slate-500 p-6 text-center text-sm">No tickets yet.</div>}
          {tickets.map((t) => (
            <button key={t.id} data-testid={`ticket-${t.id}`} onClick={()=>loadOne(t.id)} className={`w-full text-left p-3 rounded-md ${selected?.id === t.id ? "bg-blue-50 border border-blue-200" : "hover:bg-slate-50"}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-slate-900 truncate">{t.subject}</div>
                <StatusBadge status={t.status} />
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{t.category} · {t.priority} · {new Date(t.created_at).toLocaleDateString()}</div>
              {isAdmin && <div className="text-xs text-slate-500 mt-0.5 truncate">{t.user_email}</div>}
            </button>
          ))}
        </div>
        <div className="md:col-span-2 card-tactile flex flex-col overflow-hidden">
          {selected ? (
            <>
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-2">
                <div>
                  <div className="font-display text-lg font-bold text-slate-900">{selected.subject}</div>
                  <div className="text-xs text-slate-500">{selected.category} · {selected.priority} · opened by {selected.user_email}</div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={selected.status} />
                  {isAdmin && (
                    <Select value={selected.status} onValueChange={setStatus}>
                      <SelectTrigger data-testid="ticket-set-status" className="h-9 w-40"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        {["open","in_progress","waiting_for_user","resolved","closed"].map(s=><SelectItem key={s} value={s}>{s.replace(/_/g," ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                <div className="p-3 bg-slate-50 rounded-md text-sm">
                  <div className="label-caps mb-1">Original</div>
                  <div className="whitespace-pre-wrap text-slate-800">{selected.description}</div>
                </div>
                {(selected.replies || []).map((r) => (
                  <div key={r.id} className={`p-3 rounded-md text-sm ${r.author_role === "admin" ? "bg-blue-50 border border-blue-200" : "bg-slate-50 border border-slate-200"}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-slate-900">{r.author_name}</span>
                      <span className="label-caps">{r.author_role}</span>
                    </div>
                    <div className="whitespace-pre-wrap text-slate-800">{r.message}</div>
                  </div>
                ))}
              </div>
              <div className="p-3 border-t border-slate-200 flex gap-2">
                <Input data-testid="ticket-reply-input" value={reply} onChange={(e)=>setReply(e.target.value)} placeholder="Write a reply…" className="h-11 flex-1"/>
                <Button data-testid="ticket-reply-btn" onClick={sendReply} className="bg-blue-600 hover:bg-blue-700 h-11"><ChatText size={16}/></Button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-500">Select a ticket</div>
          )}
        </div>
      </div>
    </div>
  );
}
